import { ChangeEvent } from 'react';
import { Block } from '@/utils/bookStructure';
import { storage } from '@/firebase';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';

const updateTextContent = (value: string, onChange: (content: Record<string, unknown>) => void) => {
    onChange({ text: value });
};

const TextEditor = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <textarea
        className="w-full min-h-[120px] rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="本文を入力"
    />
);

const SubheadingEditor = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <input
        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold focus:border-emerald-400 focus:outline-none"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="中見出しを入力"
    />
);

const NoteEditor = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <textarea
        className="w-full min-h-[90px] rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 focus:outline-none"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="注釈を入力"
    />
);

const ListEditor = ({ items, onChange }: { items: string[]; onChange: (items: string[]) => void }) => {
    const handleItemChange = (index: number, value: string) => {
        const next = [...items];
        next[index] = value;
        onChange(next);
    };

    return (
        <div className="space-y-2">
            {items.map((item, index) => (
                <div key={`item-${index}`} className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">•</span>
                    <input
                        className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={item}
                        onChange={(event) => handleItemChange(index, event.target.value)}
                    />
                    <button
                        type="button"
                        className="text-xs text-red-500"
                        onClick={() => onChange(items.filter((_, idx) => idx !== index))}
                    >
                        削除
                    </button>
                </div>
            ))}
            <button
                type="button"
                className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600"
                onClick={() => onChange([...items, ''])}
            >
                行を追加
            </button>
        </div>
    );
};

const TableEditor = ({
    rows,
    onChange,
}: {
    rows: string[][];
    onChange: (rows: string[][]) => void;
}) => {
    const handleCellChange = (rowIndex: number, colIndex: number, value: string) => {
        const next = rows.map((row) => [...row]);
        next[rowIndex][colIndex] = value;
        onChange(next);
    };

    const addRow = () => {
        const columns = rows[0]?.length ?? 2;
        onChange([...rows, Array.from({ length: columns }, () => '')]);
    };

    const addColumn = () => {
        onChange(rows.map((row) => [...row, '']));
    };

    const removeRow = (index: number) => {
        onChange(rows.filter((_, rowIndex) => rowIndex !== index));
    };

    const removeColumn = (index: number) => {
        onChange(rows.map((row) => row.filter((_, colIndex) => colIndex !== index)));
    };

    return (
        <div className="space-y-3">
            <div className="overflow-x-auto">
                <table className="min-w-full border text-xs">
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={`row-${rowIndex}`}>
                                {row.map((cell, colIndex) => (
                                    <td key={`cell-${rowIndex}-${colIndex}`} className="border p-1">
                                        <input
                                            className="w-full rounded border border-gray-200 px-2 py-1 text-xs"
                                            value={cell}
                                            onChange={(event) => handleCellChange(rowIndex, colIndex, event.target.value)}
                                        />
                                    </td>
                                ))}
                                <td className="border px-2 py-1 text-xs text-gray-500">
                                    <button type="button" onClick={() => removeRow(rowIndex)}>
                                        行削除
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length > 0 && (
                            <tr>
                                {rows[0].map((_, colIndex) => (
                                    <td key={`remove-col-${colIndex}`} className="border px-2 py-1 text-xs text-gray-500">
                                        <button type="button" onClick={() => removeColumn(colIndex)}>
                                            列削除
                                        </button>
                                    </td>
                                ))}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600"
                    onClick={addRow}
                >
                    行追加
                </button>
                <button
                    type="button"
                    className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600"
                    onClick={addColumn}
                >
                    列追加
                </button>
            </div>
        </div>
    );
};

const ImageUploader = ({
    url,
    caption,
    alt,
    onChange,
    storagePath,
}: {
    url: string;
    caption: string;
    alt: string;
    storagePath: string;
    onChange: (content: Record<string, unknown>) => void;
}) => {
    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files?.[0]) return;
        const file = event.target.files[0];
        const fileRef = storageRef(storage, `${storagePath}/${file.name}`);
        await uploadBytes(fileRef, file, { contentType: file.type });
        const downloadUrl = await getDownloadURL(fileRef);
        onChange({ url: downloadUrl, caption, alt });
    };

    return (
        <div className="space-y-2">
            <input type="file" accept="image/*" onChange={handleFileChange} />
            {url ? <img src={url} alt={alt || '画像'} className="max-h-48 rounded-md border" /> : null}
            <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={caption}
                onChange={(event) => onChange({ url, caption: event.target.value, alt })}
                placeholder="キャプション"
            />
            <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={alt}
                onChange={(event) => onChange({ url, caption, alt: event.target.value })}
                placeholder="代替テキスト"
            />
        </div>
    );
};

type DynamicBlockFactoryProps = {
    block: Block;
    onChange: (content: Record<string, unknown>) => void;
    storagePath: string;
};

export default function DynamicBlockFactory({ block, onChange, storagePath }: DynamicBlockFactoryProps) {
    const content = block.content || {};

    if (block.type === 'paragraph') {
        return <TextEditor value={String((content as any).text || '')} onChange={(value) => updateTextContent(value, onChange)} />;
    }

    if (block.type === 'subheading') {
        return <SubheadingEditor value={String((content as any).text || '')} onChange={(value) => updateTextContent(value, onChange)} />;
    }

    if (block.type === 'note') {
        return <NoteEditor value={String((content as any).text || '')} onChange={(value) => updateTextContent(value, onChange)} />;
    }

    if (block.type === 'list') {
        const items = Array.isArray((content as any).items) ? (content as any).items : [''];
        return <ListEditor items={items} onChange={(itemsValue) => onChange({ items: itemsValue })} />;
    }

    if (block.type === 'table') {
        const rows = Array.isArray((content as any).rows) && (content as any).rows.length > 0 ? (content as any).rows : [['', '']];
        return <TableEditor rows={rows} onChange={(rowsValue) => onChange({ rows: rowsValue })} />;
    }

    if (block.type === 'image') {
        return (
            <ImageUploader
                url={String((content as any).url || '')}
                caption={String((content as any).caption || '')}
                alt={String((content as any).alt || '')}
                storagePath={storagePath}
                onChange={onChange}
            />
        );
    }

    return <TextEditor value={String((content as any).text || '')} onChange={(value) => updateTextContent(value, onChange)} />;
}
